import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getDepartmentName, getMunicipalityName } from "@pahcertificar/certmanager-locations";
import { useUpdateSubscriber } from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input } from "@/components/ui/Input";
import { LocationPicker } from "@/components/ui/LocationPicker";
import { PersonNameFields, suggestPersonName } from "@/components/PersonNameFields";
import type { Subscriber, UpdateSubscriberInput } from "@/types";

export function InfoTab({ subscriber }: { subscriber: Subscriber }) {
  const [form, setForm] = useState<UpdateSubscriberInput>({});
  const [editing, setEditing] = useState(false);
  const update = useUpdateSubscriber(subscriber.id);

  useEffect(() => {
    setForm({
      name: subscriber.name,
      firstName: subscriber.firstName ?? "",
      secondName: subscriber.secondName ?? "",
      firstSurname: subscriber.firstSurname ?? "",
      secondSurname: subscriber.secondSurname ?? "",
      email: subscriber.email,
      phone: subscriber.phone ?? "",
      address: subscriber.address,
      city: subscriber.city,
      state: subscriber.state,
      country: subscriber.country,
      department: subscriber.department ?? "",
    });
  }, [subscriber]);

  function set<K extends keyof UpdateSubscriberInput>(key: K, value: UpdateSubscriberInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: UpdateSubscriberInput =
      subscriber.type === "juridica"
        ? {
            name: form.name,
            email: form.email,
            phone: form.phone,
            address: form.address,
            city: form.city,
            state: form.state,
            country: form.country,
            department: form.department,
          }
        : {
            firstName: form.firstName?.trim() || undefined,
            secondName: form.secondName?.trim() || null,
            firstSurname: form.firstSurname?.trim() || undefined,
            secondSurname: form.secondSurname?.trim() || null,
            email: form.email,
            phone: form.phone,
            address: form.address,
            city: form.city,
            state: form.state,
            country: form.country,
            department: form.department,
          };
    try {
      await update.mutateAsync(payload);
      toast.success("Subscriber updated");
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to update");
    }
  }

  if (!editing) {
    return (
      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Subscriber information</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Row label="Name" value={subscriber.name} />
          {subscriber.type === "natural" && (
            <>
              <Row label="Primer nombre" value={subscriber.firstName} />
              <Row label="Segundo nombre" value={subscriber.secondName} />
              <Row label="Primer apellido" value={subscriber.firstSurname} />
              <Row label="Segundo apellido" value={subscriber.secondSurname} />
            </>
          )}
          <Row
            label="Document"
            value={`${subscriber.documentType} ${subscriber.documentNumber}${
              subscriber.verificationDigit ? `-${subscriber.verificationDigit}` : ""
            }`}
          />
          <Row label="Email" value={subscriber.email} />
          <Row label="Phone" value={subscriber.phone} />
          <Row label="Address" value={subscriber.address} />
          <Row
            label="Municipio"
            value={
              getMunicipalityName(subscriber.city)
                ? `${getMunicipalityName(subscriber.city)} (${subscriber.city})`
                : subscriber.city
            }
          />
          <Row
            label="Departamento"
            value={
              getDepartmentName(subscriber.state)
                ? `${getDepartmentName(subscriber.state)} (${subscriber.state})`
                : subscriber.state
            }
          />
          <Row label="Country" value={subscriber.country} />
          {subscriber.type === "juridica" && (
            <Row label="Company department" value={subscriber.department} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>Edit subscriber</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subscriber.type === "juridica" ? (
            <Field label="Business name">
              <Input
                value={form.name ?? ""}
                onChange={(e) => set("name", e.target.value)}
                maxLength={300}
              />
            </Field>
          ) : (
            <>
              <PersonNameFields
                value={{
                  firstName: form.firstName ?? "",
                  secondName: form.secondName ?? "",
                  firstSurname: form.firstSurname ?? "",
                  secondSurname: form.secondSurname ?? "",
                }}
                onChange={(next) =>
                  setForm((f) => ({
                    ...f,
                    firstName: next.firstName,
                    secondName: next.secondName,
                    firstSurname: next.firstSurname,
                    secondSurname: next.secondSurname,
                  }))
                }
                hint={!subscriber.firstName ? subscriber.name : null}
              />
              {!subscriber.firstName && subscriber.name ? (
                <div className="md:col-span-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const suggested = suggestPersonName(subscriber.name);
                      setForm((f) => ({ ...f, ...suggested }));
                    }}
                  >
                    Suggest split from stored name
                  </Button>
                </div>
              ) : null}
            </>
          )}
          <Field label="Email">
            <Input
              type="email"
              value={form.email ?? ""}
              onChange={(e) => set("email", e.target.value)}
              maxLength={256}
            />
          </Field>
          <Field label="Phone">
            <Input
              value={form.phone ?? ""}
              onChange={(e) => set("phone", e.target.value)}
              maxLength={30}
            />
          </Field>
          <Field label="Address" className="md:col-span-2">
            <Input
              value={form.address ?? ""}
              onChange={(e) => set("address", e.target.value)}
              maxLength={300}
            />
          </Field>
          <div className="md:col-span-2">
            <LocationPicker
              departmentCode={form.state || null}
              municipalityCode={form.city || null}
              onDepartmentChange={(code) => set("state", code ?? "")}
              onMunicipalityChange={(code) => set("city", code ?? "")}
              required
            />
          </div>
          <Field label="Country" hint="Always CO" className="md:col-span-2">
            <Input value="CO" readOnly disabled />
          </Field>
          {subscriber.type === "juridica" && (
            <Field label="Company department" className="md:col-span-2">
              <Input
                value={form.department ?? ""}
                onChange={(e) => set("department", e.target.value)}
                maxLength={200}
              />
            </Field>
          )}
        </CardContent>
        <CardFooter>
          <Button type="button" variant="outline" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button type="submit" loading={update.isPending}>
            Save changes
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-slate-900 mt-0.5">{value || <span className="text-slate-400">—</span>}</p>
    </div>
  );
}
